/**
 * Alerts API
 *
 * Handles all alert-related API calls.
 * Uses Zod validation for critical endpoints.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  alertEventsListResponseSchema,
  alertRulesListResponseSchema,
  alertEventSchema,
  alertAttentionMomentResponseSchema,
  alertCriterionEvidenceResponseSchema,
  bulkResolveAlertsResponseSchema,
  type AlertAttentionMomentResponse,
  type AlertCriterionEvidenceResponse,
  type AlertEvent,
  type BulkResolveAlertsRequest,
  type BulkResolveAlertsResponse,
} from '@aica/api-types/schemas';
import type {
  Alert,
  AlertsListResponse,
  AlertRule,
  AlertRulesListResponse,
  CreateAlertRuleRequest,
} from '../types';

// Upper bound on a single alerts fetch. Kept high so the Alerts page can
// drive bulk actions and search across recent events without server-side
// pagination; revisit when proper pagination is wired through the UI.
const ALERTS_LIST_PAGE_SIZE = 1000;

export class AlertsApi extends BaseApiClient {
  // ============================================================================
  // ALERTS
  // ============================================================================

  /**
   * Get alerts list - validated with Zod
   */
  async getAlerts(params?: {
    limit?: number;
    offset?: number;
    status?: 'active' | 'acknowledged' | 'resolved';
    severity?: 'low' | 'medium' | 'high' | 'critical';
    /** One project — the route takes no list (the unified dashboard's count, #1337). */
    projectId?: string;
    /** The route's demo policy; absent — its default, demo included. */
    includeDemo?: 'yes' | 'no';
  }): Promise<AlertsListResponse> {
    const query = this.buildQuery({
      limit: params?.limit ?? ALERTS_LIST_PAGE_SIZE,
      offset: params?.offset,
      status: params?.status,
      severity: params?.severity,
      project_id: params?.projectId,
      include_demo: params?.includeDemo,
    });
    const validated = await this.validatedRequest(
      `${API_ENDPOINTS.ALERTS}${query}`,
      alertEventsListResponseSchema
    );
    return validated as unknown as AlertsListResponse;
  }

  /**
   * Resolve every alert matching the filter set.
   * Empty filter → resolves all currently active alerts in the org.
   */
  async bulkResolveAlerts(
    filters: BulkResolveAlertsRequest
  ): Promise<BulkResolveAlertsResponse> {
    const validated = await this.validatedRequest(
      API_ENDPOINTS.ALERTS_BULK_RESOLVE,
      bulkResolveAlertsResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(filters),
      }
    );
    return validated as unknown as BulkResolveAlertsResponse;
  }

  /**
   * Get alert by ID - validated with Zod
   */
  async getAlertById(id: string): Promise<Alert> {
    const validated = await this.validatedRequest<AlertEvent>(
      API_ENDPOINTS.ALERTS_BY_ID(id),
      alertEventSchema
    );
    return validated as unknown as Alert;
  }

  /**
   * The quote(s) of the call criteria the alert's rule read, for the in-app
   * card only (#1800). Resolved by the server at read time under the call
   * panel's access — the alert event itself carries no quote.
   */
  async getAlertCriterionEvidence(id: string): Promise<AlertCriterionEvidenceResponse> {
    const validated = await this.validatedRequest(
      API_ENDPOINTS.ALERTS_CRITERION_EVIDENCE(id),
      alertCriterionEvidenceResponseSchema
    );
    return validated as AlertCriterionEvidenceResponse;
  }

  /**
   * The key moment an alert's attention-flag reason stands on, for the in-app
   * card only (#2002). Resolved by the server at read time under the analysis
   * card's access — the alert event itself carries the reason alone. `moment`
   * is `null` whenever there is nothing this viewer may be shown.
   */
  async getAlertAttentionMoment(id: string): Promise<AlertAttentionMomentResponse> {
    const validated = await this.validatedRequest(
      API_ENDPOINTS.ALERTS_ATTENTION_MOMENT(id),
      alertAttentionMomentResponseSchema
    );
    return validated as AlertAttentionMomentResponse;
  }

  async acknowledgeAlert(id: string): Promise<Alert> {
    return this.request<Alert>(API_ENDPOINTS.ALERTS_ACKNOWLEDGE(id), {
      method: 'PATCH',
    });
  }

  async resolveAlert(id: string): Promise<Alert> {
    return this.request<Alert>(API_ENDPOINTS.ALERTS_RESOLVE(id), {
      method: 'PATCH',
    });
  }

  async createCrmFollowUp(
    id: string,
    data: {
      actionType: 'todo' | 'scheduled_call';
      deadline: string;
      assigneeId?: string;
    }
  ): Promise<{
    state: 'succeeded';
    externalActivityId: string;
    recovered: boolean;
    status: 'active' | 'acknowledged' | 'resolved';
  }> {
    return this.request(API_ENDPOINTS.ALERTS_CRM_ACTION(id), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================================================
  // ALERT RULES
  // ============================================================================

  /**
   * Get alert rules list - validated with Zod
   */
  async getAlertRules(): Promise<AlertRulesListResponse> {
    const validated = await this.validatedRequest(
      API_ENDPOINTS.ALERTS_RULES,
      alertRulesListResponseSchema
    );
    return validated as unknown as AlertRulesListResponse;
  }

  async createAlertRule(data: CreateAlertRuleRequest): Promise<AlertRule> {
    return this.request<AlertRule>(API_ENDPOINTS.ALERTS_RULES, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAlertRule(
    id: string,
    data: {
      name?: string;
      description?: string;
      category?: string;
      severity?: string;
      ruleJson?: Record<string, unknown>;
      isEnabled?: boolean;
      throttleSeconds?: number;
      // When disabling, also resolve every still-active event from this rule.
      cleanupActiveAlerts?: boolean;
    }
  ): Promise<AlertRule & { resolvedCount?: number }> {
    return this.request<AlertRule & { resolvedCount?: number }>(
      API_ENDPOINTS.ALERTS_RULES_BY_ID(id),
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  async deleteAlertRule(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(API_ENDPOINTS.ALERTS_RULES_BY_ID(id), {
      method: 'DELETE',
    });
  }
}
